import { DAEMON_URL } from "../config";
import { HttpError } from "./errors";
import type { ConversationRow, ConversationSummary, TurnRow } from "./types";

/**
 * Read a non-2xx JSON error body into `{ message, code }`, mirroring
 * `client.ts`'s readError. The point is that a machine code the daemon sends
 * (`invalid_branch`, `branch_exists`, `worktree_failed`) survives to the caller
 * instead of collapsing into a bare `http N`, which is the fallback only when
 * the body is missing or is not JSON.
 *
 * The daemon writes two shapes. A lone `error` is prose
 * (`{ error: "conversation has no branch to rename" }`, src/server.ts:2235).
 * When `message` is present alongside it, `error` is the machine code and
 * `message` is the sentence (`{ error: "branch_exists", message: "branch ai/x
 * already exists" }`, src/server.ts:2251). The branch rename UI renders
 * `message` inline, so the code must never be what lands in `HttpError.message`
 * — otherwise the user reads the token "branch_exists".
 */
async function readError(res: Response): Promise<{ message: string; code?: string }> {
  try {
    const data = (await res.json()) as { error?: unknown; message?: unknown; code?: unknown };
    const error = typeof data.error === "string" && data.error.length > 0 ? data.error : undefined;
    const detail = typeof data.message === "string" && data.message.length > 0 ? data.message : undefined;
    const message = detail ?? error ?? `http ${res.status}`;
    // An explicit `code` still wins. Otherwise `error` is the code only when
    // `message` supplied the prose, never when `error` IS the prose.
    const code = typeof data.code === "string" ? data.code : detail ? error : undefined;
    return { message, code };
  } catch {
    return { message: `http ${res.status}` };
  }
}

async function doFetch(url: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) {
    const { message, code } = await readError(res);
    throw new HttpError(res.status, message, code);
  }
  return res;
}

export async function listConversations(
  project: string,
): Promise<ConversationSummary[]> {
  const headers = { "Accept": "application/json" };
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}`;
  const res = await doFetch(url, { method: "GET", headers, credentials: "include" });
  const body = (await res.json()) as { conversations: ConversationSummary[] };
  return body.conversations;
}

export async function getTurns(
  project: string,
  conversationId: number,
): Promise<TurnRow[]> {
  const headers = { "Accept": "application/json" };
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/${encodeURIComponent(String(conversationId))}/turns`;
  const res = await doFetch(url, { method: "GET", headers, credentials: "include" });
  const body = (await res.json()) as { turns: TurnRow[] };
  return body.turns;
}

export async function createConversation(
  project: string,
  opts: { branch?: string; baseBranch?: string; attach?: boolean; title?: string } = {},
): Promise<ConversationRow> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}`;
  const res = await doFetch(url, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(opts),
  });
  const body = (await res.json()) as { conversation: ConversationRow };
  return body.conversation;
}

/** Back-compat alias for existing callers. */
export const startNewConversation = (project: string): Promise<ConversationRow> => createConversation(project);

export async function archiveConversation(project: string, conversationId: number): Promise<void> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/${conversationId}`;
  await doFetch(url, { method: "DELETE", headers: { Accept: "application/json" }, credentials: "include" });
}

/**
 * Rename a conversation (set its custom title). An empty string clears the
 * title and the sidebar falls back to the first-message preview. Returns the
 * stored title (null when cleared).
 */
export async function renameConversation(
  project: string,
  conversationId: number,
  title: string,
): Promise<string | null> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/${conversationId}`;
  const res = await doFetch(url, {
    method: "PATCH",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ title }),
  });
  const body = (await res.json()) as { title: string | null };
  return body.title ?? null;
}

/**
 * Save the sidebar order for this project: `ids` top first. The daemon keeps
 * every chat not named here after these, in its current order.
 */
export async function reorderConversations(project: string, ids: number[]): Promise<void> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/order`;
  await doFetch(url, {
    method: "PUT",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ ids }),
  });
}

/**
 * Branch names for the chat branch picker, read from the project's LOCAL
 * primary clone — so a branch an open conversation created but never pushed is
 * offered too. `defaultBranch` is first in `branches` and is not repeated.
 *
 * The daemon degrades to a list holding just the default branch when the clone
 * is absent, so a 200 never carries an empty list.
 */
export async function listBranches(
  project: string,
): Promise<{ branches: string[]; defaultBranch: string }> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/branches`;
  const res = await doFetch(url, { method: "GET", headers: { Accept: "application/json" }, credentials: "include" });
  return (await res.json()) as { branches: string[]; defaultBranch: string };
}

/**
 * Rename the git branch backing a conversation. The worktree does not move:
 * the daemon renames in place and pins the directory, so a caller already
 * showing the worktree path does not need to recompute it.
 *
 * Failures arrive as `HttpError` — `doFetch` throws before this returns, which
 * is why there is no `res.ok` branch here. By then the body has been read into
 * `message`/`code`, so a 409 surfaces the daemon's own sentence ("branch ai/x
 * already exists", "cannot rename the branch while a turn is streaming") with
 * `code` set to `branch_exists` / `in_flight` for the caller to branch on.
 *
 * Returns the stored branch name and the pinned worktree path. A rename is the
 * moment `worktree_dir` goes from NULL to set, so returning the path lets a
 * caller update its display immediately instead of refetching the list for a
 * value the daemon already handed back.
 */
export async function renameConversationBranch(
  project: string,
  conversationId: number,
  branch: string,
): Promise<{ branch: string; worktreeDir?: string }> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/${conversationId}`;
  const res = await doFetch(url, {
    method: "PATCH",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ branch }),
  });
  // `branch` is what the daemon stored — fall back to the requested name only
  // if a future daemon stops echoing it. `worktreeDir` stays optional because
  // the same-name early return answers without it, so a caller must treat an
  // absent path as "unchanged", never as "cleared".
  const body = (await res.json()) as { branch?: string; worktreeDir?: string };
  return { branch: body.branch ?? branch, worktreeDir: body.worktreeDir };
}

/** Phase 4: clone someone else's conversation (typically the bot's) into a
 * fresh conversation owned by the calling user. The cloned conversation is
 * set active on the daemon so the next /chat message lands inside it. */
export async function copyConversation(
  project: string,
  sourceId: number,
): Promise<{ conversation: ConversationRow; turnsCopied: number }> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/${sourceId}/copy`;
  const res = await doFetch(url, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  return (await res.json()) as { conversation: ConversationRow; turnsCopied: number };
}
